import { currentAppVersion } from './appVersion';
import { useOnlineStore } from '../store/onlineStore';
import { createAppVersionInfo, type AppVersionInfo } from './versioning';
import { apiFetch, getAppPlatform } from './platform';

export type VersionCheckInfo = AppVersionInfo & {
  forceUpgrade?: boolean;
  minSupportedNativeBuild?: number | null;
  platform?: string;
  storeUrl?: string | null;
  storeUpdatePromptsEnabled?: boolean;
};

/**
 * Checks the current version against the server version.
 * @returns {Promise<AppVersionInfo | undefined>} The current version metadata from the server, or undefined on error.
 * @async
 */
const checkVersion = async (): Promise<VersionCheckInfo | undefined> => {
  try {
    const response = await apiFetch('/api/check_version', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        version: currentAppVersion.version,
        buildNumber: currentAppVersion.buildNumber,
        displayVersion: currentAppVersion.displayVersion,
        dev: import.meta.env.DEV,
        platform: getAppPlatform(),
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    useOnlineStore.setState({ isOnline: true });

    const data = await response.json();
    if (!data?.version) {
      return;
    }

    return {
      ...createAppVersionInfo(data.version, data.buildNumber),
      forceUpgrade: Boolean(data.forceUpgrade),
      minSupportedNativeBuild:
        typeof data.minSupportedNativeBuild === 'number' ? data.minSupportedNativeBuild : null,
      platform: typeof data.platform === 'string' ? data.platform : undefined,
      storeUrl: typeof data.storeUrl === 'string' ? data.storeUrl : null,
      storeUpdatePromptsEnabled: Boolean(data.storeUpdatePromptsEnabled),
    };
  } catch (error) {
    useOnlineStore.setState({ isOnline: false });
    console.error('Error checking version:', error);
    return;
  }
};

/**
 * Fetches translations from the server for the specified language.
 * @param lang - The language code.
 * @returns A promise that resolves to the translations object.
 */
const getTranslationsFromServer = async (lang: string): Promise<Record<string, string>> => {
  const response = await apiFetch('/api/translate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ lang: lang.toLowerCase().slice(0, 2) }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch translations');
  }

  const data = await response.json();
  console.log('data', data);
  return data;
};

export { checkVersion, getTranslationsFromServer };
